import type { ComponentPropsWithoutRef, CSSProperties, ReactNode } from "react";
import styled from "styled-components";

import { controlTransition } from "../foundations/controlStyles";
import { targetColorCss } from "./targetColor";
import type { AnnotationTarget } from "./types";

const INDICATOR_SLOT_COUNT = 9;

const Root = styled.span`
  position: relative;
  box-sizing: border-box;
  display: grid;
  width: 22px;
  height: 22px;
  aspect-ratio: 1;
  grid-template-columns: repeat(3, 6px);
  grid-template-rows: repeat(3, 6px);
  gap: 1px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.border.strong};
  background: ${({ theme }) => theme.color.border.default};
`;

const Slot = styled.span<{ $occupied: boolean }>`
  box-sizing: border-box;
  width: 6px;
  height: 6px;
  aspect-ratio: 1;
  border: 0;
  background: ${({ $occupied, theme }) =>
    $occupied
      ? "var(--c6-frame-annotation-color)"
      : `var(--c6-frame-annotation-empty-background, ${theme.color.bg.surfaceSunken})`};
  ${controlTransition("background-color")}
`;

export interface FrameAnnotationGridProps extends Omit<
  ComponentPropsWithoutRef<"span">,
  "aria-hidden" | "children"
> {
  readonly targets: readonly AnnotationTarget[];
  readonly annotatedTargetIds: ReadonlySet<string>;
  readonly children?: ReactNode;
}

export function FrameAnnotationGrid({
  targets,
  annotatedTargetIds,
  children,
  ...props
}: FrameAnnotationGridProps) {
  return (
    <Root {...props} aria-hidden="true" data-frame-annotation-grid="">
      {Array.from({ length: INDICATOR_SLOT_COUNT }, (_, slotIndex) => {
        const target = targets[slotIndex];
        const occupied = target ? annotatedTargetIds.has(target.id) : false;
        return (
          <Slot
            key={target?.id ?? `empty-${slotIndex}`}
            $occupied={occupied}
            data-frame-annotation-slot={occupied ? "occupied" : "empty"}
            style={
              target
                ? ({
                    "--c6-frame-annotation-color": targetColorCss(target.color),
                  } as CSSProperties)
                : undefined
            }
          />
        );
      })}
      {children}
    </Root>
  );
}
